import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Layout,
  Input as AntInput,
  Row,
} from "antd";
import { Modal, Form, message } from "antd";
import type { InputRef } from "antd";
import moment from "moment";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import {
  DELETE_MEMBER,
  ENABLE_MEMBER,
  FETCH_ALL_MEMBERS,
  TOGGLE_MEMBER_ROLE,
} from "../../services/apiEndPoints";
import { Typography } from "antd";
import { IMember } from "../types/member.types";
import MemberInviteComponent from "./MemberInviteComponent";
import axios from "../../services/axiosInstance";
const { Title } = Typography;

const MemberTableComponent = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [data, setData] = useState<IMember[]>([]);
  const searchInput = useRef<InputRef>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteForm] = Form.useForm();

  const fetchMembers = async () => {
    const members = await axios.get(FETCH_ALL_MEMBERS);

    if (members && members.data) {
      setData(members.data);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleSearch = useMemo(
    () =>
      debounce((selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
      }, 300),
    []
  );

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const handleEdit = (member: IMember) => {
    inviteForm.setFieldsValue(member);
    setIsInviteModalOpen(true);
  };

  const deleteMember = async (memberId: string) => {
    return axios.delete(`${DELETE_MEMBER}/${memberId}`);
  };

  const enableMember = async (memberId: string) => {
    return axios.put(`${ENABLE_MEMBER}/${memberId}`, {});
  };

  const handleDelete = async (memberId: string) => {
    try {
      await deleteMember(memberId);
      message.success("Member deleted successfully");
      await fetchMembers();
    } catch (error) {
      message.error("Failed to delete member");
    }
  };

  const handleEnable = async (memberId: string) => {
    try {
      await enableMember(memberId);
      message.success("Member enabled successfully");
      await fetchMembers();
    } catch (error) {
      message.error("Failed to enable member");
    }
  };

  const toggleMemberRole = async (memberId: string, role: string) => {
    return axios.put(`${TOGGLE_MEMBER_ROLE(memberId)}`, { role });
  };

  const handleToggleRole = async (memberId: string, role: string) => {
    try {
      await toggleMemberRole(memberId, role);
      message.success("Member role updated successfully");
      await fetchMembers();
    } catch (error) {
      message.error("Failed to update member role");
    }
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters = () => {},
    }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8 }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => handleReset(clearFilters)}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: any) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value: any, record: IMember) =>
      record[dataIndex as keyof IMember]
        ?.toString()
        .toLowerCase()
        .includes(String(value).toLowerCase()),
    onFilterDropdownOpenChange: (visible: any) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text?.toString() || ""}
        />
      ) : (
        text
      ),
  });

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "memberName",
        key: "memberName",
        ...getColumnSearchProps("memberName"),
      },
      {
        title: "Email",
        dataIndex: "memberEmail",
        key: "memberEmail",
        ...getColumnSearchProps("memberEmail"),
      },
      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (createdAt: string) => (
          <Typography.Text>
            {moment(createdAt).format("MMM D, YYYY")}
          </Typography.Text>
        ),
      },
      {
        title: "Verified",
        dataIndex: "isVerified",
        key: "isVerified",
        render: (isVerified: boolean) => (
          <Typography.Text>{isVerified ? "Yes" : "No"}</Typography.Text>
        ),
      },
      {
        title: "Role",
        dataIndex: "role",
        key: "role",
        render: (role: string, record: IMember) => (
          <Tag
            color={role === "admin" ? "red" : "blue"}
            style={{ cursor: "pointer" }}
            onClick={() => {
              Modal.confirm({
                title: "Change Role",
                content: `Are you sure you want to change this member's role to ${
                  role === "admin" ? "USER" : "ADMIN"
                }?`,
                okText: "Yes",
                onOk: () =>
                  handleToggleRole(
                    record.memberId,
                    role === "admin" ? "user" : "admin"
                  ),
              });
            }}
          >
            {role.toUpperCase()}
          </Tag>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_: any, record: IMember) => (
          <Space>
            {/* <Button
                            type="link"
                            onClick={() => handleEdit(record)}
                        >
                            Edit
                        </Button> */}

            {record.isActive ? (
              <Button
                type="link"
                danger
                onClick={() => {
                  Modal.confirm({
                    title: "Delete Member",
                    content: "Are you sure you want to delete this member?",
                    okText: "Delete",
                    okType: "danger",
                    onOk: () => handleDelete(record.memberId),
                  });
                }}
              >
                Delete
              </Button>
            ) : (
              <Button
                ghost
                type="link"
                onClick={() => {
                  Modal.success({
                    title: "Enable Member",
                    content: "Are you sure you want to enable this member?",
                    okText: "Enable",
                    okType: "default",
                    onOk: () => handleEnable(record.memberId),
                  });
                }}
              >
                Enable
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [searchText, searchedColumn]
  );

  return (
    <Layout>
      <Row justify="space-between" align="middle">
        <Title level={2} style={{ margin: "16px 0" }}>
          Member
        </Title>
        {MemberInviteComponent(
          inviteForm,
          isInviteModalOpen,
          setIsInviteModalOpen,
          fetchMembers
        )}
        <Space>
          <Button type="primary" onClick={() => setIsInviteModalOpen(true)}>
            Invite Member
          </Button>
        </Space>
      </Row>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        pagination={{ pageSize: 10 }}
      />
    </Layout>
  );
};

export default MemberTableComponent;
